import { Injectable, Signal, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClientWrapperService } from '../http/http-client-wrapper.service';
import { Observable } from 'rxjs';
import { EditOrder, Order } from 'src/app/core/models/Order';
import { ApiUrlService } from '../api/api-url.service';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  private readonly apiUrlService = inject(ApiUrlService);
  private get ordersApiUrl(): string { return this.apiUrlService.buildUrl(`/api/v1/orders`); }

  private readonly _charityOrders = signal<Order[]>([]);
  readonly charityOrders: Signal<Order[]> = this._charityOrders.asReadonly();
  readonly charityOrders$ = toObservable(this._charityOrders);

  private readonly _businessOrders = signal<Order[]>([]);
  readonly businessOrders: Signal<Order[]> = this._businessOrders.asReadonly();
  readonly businessOrders$ = toObservable(this._businessOrders);

  constructor(private httpClient: HttpClientWrapperService) {}

  getOrdersByUserId(userId: string): Observable<Order[]> {
    return this.httpClient.get<Order[]>(`${this.ordersApiUrl}?userId=${userId}`);
  }

  getOrdersByCharityId(charityId: string): Observable<Order[]> {
    return this.httpClient.get<Order[]>(`${this.ordersApiUrl}?charityId=${charityId}`);
  }

  getOrdersByProductIdAndCharityId(productId: string, charityId: string): Observable<Order[]> {
    return this.httpClient.get<Order[]>(`${this.ordersApiUrl}?charityId=${charityId}&productId=${productId}`);
  }

  getOrdersByProductIdAndUserId(productId: string, userId: string): Observable<Order[]> {
    return this.httpClient.getGuest<Order[]>(`${this.ordersApiUrl}?userId=${userId}&productId=${productId}`);
  }

  loadOrdersByCharityId(charityId: string): void {
    this.getOrdersByCharityId(charityId).subscribe({
      next: (orders: Order[]) => this._charityOrders.set(orders),
      error: (err) => console.error('Failed to fetch charity orders:', err)
    });
  }

  getOrdersByOrderId(orderId: string): Observable<Order> {
    return this.httpClient.getGuest<Order>(`${this.ordersApiUrl}/${orderId}`);
  }

  fulfillOrderByOrderId(orderId: string): Observable<void> {
    return this.httpClient.put<void, null>(`${this.ordersApiUrl}/${orderId}/fulfill`, null);
  }

  editOrderByOrderId(orderId: string, order: EditOrder): Observable<Order> {
    return this.httpClient.put<Order, EditOrder>(`${this.ordersApiUrl}/${orderId}`, order);
  }

  getOrdersByBusinessId(businessId: number, fulfilled?: boolean): Observable<Order[]> {
    let url: string = `${this.ordersApiUrl}?businessId=${businessId}`;
    if (fulfilled !== undefined) {
      url += `&isFulfilled=${fulfilled}`;
    }
    return this.httpClient.get<Order[]>(url);
  }

  loadOrdersByBusinessId(businessId: number, fulfilled?: boolean): void {
    this.getOrdersByBusinessId(businessId, fulfilled).subscribe({
      next: (orders: Order[]) => this._businessOrders.set(orders),
      error: (err) => console.error('Failed to fetch business orders:', err)
    });
  }
}
